import firebase from 'firebase';
import { trackedFetch } from '../../utils/sentry/apiTracking';
import { logErrorWithContext } from '../../utils/sentry/sentrySetup';

const DEFAULT_MAX_RETRIES = 3;

/** An Error from a non-2xx response, carrying the status that produced it. */
export interface ApiError extends Error {
  status?: number;
}

export default class API {
  private headers: { [key: string]: string } | null = null;

  constructor(headers?: { [key: string]: string }) {
    this.headers = headers || null;

    this.getJSON = this.getJSON.bind(this);
    this.setHeaders = this.setHeaders.bind(this);
  }

  static async getToken() {
    const user = firebase.auth().currentUser;
    if (!user) {
      throw new Error('unauthorized');
    }

    const token = await user.getIdToken();

    return token;
  }

  /**
   * Create an API instance pre-loaded with the current user's Bearer token.
   * Preferred over manually calling getToken() + new API({ Authorization: ... }).
   *
   * @example
   * const api = await API.withToken();
   * const res = await api.getJSON(url);
   */
  static async withToken(): Promise<API> {
    const token = await API.getToken();
    return new API({ Authorization: `Bearer ${token}` });
  }

  static makeQueryString(obj: { [k: string]: string | null | undefined }) {
    return Object.entries(obj)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v!)}`)
      .join('&');
  }

  setHeaders(headers: { [key: string]: string }) {
    this.headers = Object.assign({}, this.headers, headers);
  }

  private async request(method: string, url: string, body?: object): Promise<any> {
    const hasBody = body !== undefined;
    const headers = hasBody ? { 'Content-Type': 'application/json', ...this.headers } : this.headers || undefined;

    const res = await trackedFetch(url, {
      method,
      headers,
      ...(hasBody && { body: JSON.stringify(body) })
    });

    const contentType = res.headers.get('Content-Type');

    if (res.status >= 400) {
      let message = `API request failed: ${res.status}`;
      try {
        const body = await res.json();
        if (typeof body?.message === 'string' && body.message.trim()) {
          message = body.message;
        }
      } catch {}
      // The status is carried on the error because callers cannot otherwise
      // tell "the server refused this" from "we could not reach the server",
      // and those need opposite handling: one is shown to the person, the
      // other must not be. Additive - the message is unchanged.
      const error: ApiError = new Error(message);
      error.status = res.status;
      logErrorWithContext(error, `api_${method.toLowerCase()}_json_error`, {
        url,
        status: res.status,
        contentType
      });
      throw error;
    }

    if (!contentType?.includes('application/json')) {
      logErrorWithContext(
        new Error(`Unexpected content type: ${contentType}`),
        `api_${method.toLowerCase()}_unexpected_content_type`,
        { url, contentType }
      );
      return null;
    }

    return res.json();
  }

  async getJSON(url: string) {
    try {
      return await this.request('GET', url);
    } catch {
      return null;
    }
  }

  async getJSONOrThrow(url: string): Promise<any> {
    return this.request('GET', url);
  }

  async postJSON(url: string, body: object): Promise<any> {
    return this.request('POST', url, body);
  }

  async putJSON(url: string, body: object): Promise<any> {
    return this.request('PUT', url, body);
  }

  async deleteJSON(url: string): Promise<any> {
    return this.request('DELETE', url);
  }

  async fetchWithRetry(url: string, options: RequestInit, maxRetries: number = DEFAULT_MAX_RETRIES): Promise<Response> {
    let lastError: Error = new Error('fetchWithRetry: no attempts made');

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const res = await trackedFetch(url, options);

        // Retry on transient server errors (429, 500-503)
        if (res.status === 429 || (res.status >= 500 && res.status <= 503)) {
          lastError = new Error(`API request failed: ${res.status}`);
          if (attempt < maxRetries) {
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          return res;
        }

        return res;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        logErrorWithContext(lastError, 'api_fetch_with_retry_exhausted', {
          url,
          maxRetries
        });
        throw lastError;
      }
    }

    // Unreachable — every loop iteration ends in return, continue, or throw.
    // Retained only for TypeScript's type-checker.
    throw lastError;
  }
}
